import * as yup from 'yup';

// Company Settings Validation Schema
export const companySettingsSchema = yup.object().shape({
  companyName: yup
    .string()
    .required('Company Name is required')
    .trim()
    .min(2, 'Company Name must be at least 2 characters'),
  contactPerson: yup.string().trim(),
  address: yup.string().trim(),
  countryId: yup.string().nullable(),
  city: yup.string().trim(),
  stateId: yup.string().nullable(),
  postalCode: yup.string().trim(),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .trim(),
  phone: yup.string().trim(),
  mobile: yup.string().trim(),
  fax: yup.string().trim(),
  website: yup
    .string()
    .url('Please enter a valid URL')
    .trim(),
});

// Email Settings Validation Schema (PHP Mail)
export const phpMailSettingsSchema = yup.object().shape({
  phpFromAddress: yup
    .string()
    .required('Email From Address is required for PHP Mail')
    .email('Please enter a valid email address')
    .trim(),
  phpFromName: yup
    .string()
    .required('Email From Name is required for PHP Mail')
    .trim()
    .min(2, 'Email From Name must be at least 2 characters'),
});

// Email Settings Validation Schema (SMTP)
export const smtpMailSettingsSchema = yup.object().shape({
  smtpHost: yup
    .string()
    .required('SMTP Host is required')
    .trim(),
  smtpUser: yup
    .string()
    .required('SMTP User is required')
    .trim(),
  smtpPassword: yup
    .string()
    .required('SMTP Password is required')
    .min(6, 'SMTP Password must be at least 6 characters'),
  smtpPort: yup
    .string()
    .required('SMTP Port is required')
    .matches(/^\d+$/, 'SMTP Port must be a number')
    .test('valid-port', 'Port must be between 1 and 65535', (value) => {
      if (!value) return false;
      const port = parseInt(value);
      return port >= 1 && port <= 65535;
    }),
  smtpSecurity: yup
    .string()
    .oneOf(['none', 'ssl', 'tls'], 'Invalid security type'),
  smtpAuthDomain: yup.string().trim(),
});

// Email Settings Validation Schema (ZeptoMail API)
export const zeptoMailSettingsSchema = yup.object().shape({
  zeptoApiToken: yup
    .string()
    .required('ZeptoMail Send Mail token is required')
    .trim(),
  zeptoFromAddress: yup
    .string()
    .required('Sender address is required')
    .email('Please enter a valid email address')
    .trim(),
  zeptoFromName: yup.string().trim(),
  zeptoApiUrl: yup.string().trim(),
});

// Test Email Validation Schema
export const testEmailSchema = yup.object().shape({
  email: yup
    .string()
    .required('Email address is required')
    .email('Please enter a valid email address')
    .trim(),
});

// Localization Settings Validation Schema
export const localizationSettingsSchema = yup.object().shape({
  defaultCountryId: yup.string().nullable(),
  dateFormat: yup
    .string()
    .required('Date Format is required')
    .trim(),
  timezone: yup
    .string()
    .required('Timezone is required')
    .trim(),
  defaultLanguage: yup
    .string()
    .required('Default Language is required')
    .trim()
    .min(2, 'Language code must be at least 2 characters')
    .max(5, 'Language code must not exceed 5 characters'),
  currencyCode: yup
    .string()
    .required('Currency Code is required')
    .trim()
    .length(3, 'Currency Code must be exactly 3 characters'),
  currencySymbol: yup
    .string()
    .required('Currency Symbol is required')
    .trim()
    .max(5, 'Currency Symbol must not exceed 5 characters'),
});

// SMS Settings Validation Schema (when enabled)
export const smsSettingsSchema = yup.object().shape({
  termiiApiKey: yup
    .string()
    .when('enableSms', {
      is: true,
      then: (schema) => schema
        .required('Termii API Key is required when SMS is enabled')
        .trim()
        .min(10, 'API Key appears to be invalid'),
      otherwise: (schema) => schema.notRequired(),
    }),
  termiiSenderId: yup
    .string()
    .when('enableSms', {
      is: true,
      then: (schema) => schema
        .required('Sender ID is required when SMS is enabled')
        .trim()
        .min(3, 'Sender ID must be at least 3 characters')
        .max(11, 'Sender ID must not exceed 11 characters')
        .matches(/^[a-zA-Z0-9]+$/, 'Sender ID must be alphanumeric'),
      otherwise: (schema) => schema.notRequired(),
    }),
  termiiChannel: yup
    .string()
    .oneOf(['generic', 'dnd', 'whatsapp'], 'Invalid channel type'),
  enableSms: yup.boolean(),
});

// Test SMS Validation Schema
export const testSmsSchema = yup.object().shape({
  phone: yup
    .string()
    .required('Phone number is required')
    .trim()
    .matches(/^\+\d{1,4}\d{6,14}$/, 'Phone number must be in international format (e.g., +234...)'),
  termiiApiKey: yup
    .string()
    .required('Termii API Key must be configured')
    .trim(),
  termiiSenderId: yup
    .string()
    .required('Sender ID must be configured')
    .trim(),
});

// Approval Settings Validation Schema
export const approvalSettingsSchema = yup.object().shape({
  requireLeaveApproval: yup.boolean(),
  requireExpenseApproval: yup.boolean(),
  requireOvertimeApproval: yup.boolean(),
  requireLoanApproval: yup.boolean(),
  autoApproveLeaveAfterDays: yup
    .number()
    .nullable()
    .min(0, 'Days must be 0 or greater')
    .max(365, 'Days must not exceed 365'),
  multiLevelApproval: yup.boolean(),
});

// Salary Settings Validation Schema
export const salarySettingsSchema = yup.object().shape({
  payPeriod: yup
    .string()
    .oneOf(['monthly', 'bi-weekly', 'weekly'], 'Invalid pay period'),
  salaryProcessingDay: yup
    .number()
    .nullable()
    .min(1, 'Day must be between 1 and 31')
    .max(31, 'Day must be between 1 and 31'),
  includeProvidentFund: yup.boolean(),
  providentFundPercentage: yup
    .number()
    .nullable()
    .min(0, 'Percentage must be 0 or greater')
    .max(100, 'Percentage must not exceed 100'),
  includeTaxDeduction: yup.boolean(),
  taxCalculationMethod: yup.string(),
  includeOvertimeInSalary: yup.boolean(),
  overtimeRateMultiplier: yup
    .number()
    .nullable()
    .min(0, 'Multiplier must be 0 or greater')
    .max(10, 'Multiplier must not exceed 10'),
});

// Notification Settings Validation Schema
export const notificationSettingsSchema = yup.object().shape({
  enableEmailNotifications: yup.boolean(),
  enableSmsNotifications: yup.boolean(),
  enablePushNotifications: yup.boolean(),
  notifyOnLeaveApplication: yup.boolean(),
  notifyOnLeaveApproval: yup.boolean(),
  notifyOnAttendanceAnomaly: yup.boolean(),
  notifyOnPayrollProcessing: yup.boolean(),
  notifyOnEmployeeBirthday: yup.boolean(),
  notifyOnDocumentExpiry: yup.boolean(),
});
